"use client";

import { cn } from "@shadcn/lib/utils";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn/ui/card";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Check, Clock } from "lucide-react";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import { type FC, type UIEventHandler, useCallback, useEffect, useRef, useState } from "react";
import AlarmService from "@/src/application/services/alarm-service";
import AlarmStore from "@/src/application/store/alarm-store";
import { StatusEnum } from "@/src/domain/enums/StatusEnum";
import type Alarm from "@/src/domain/models/alarm";
import useInject from "@/src/presentation/hooks/useInject";
import { getRelativeTime } from "@/src/shared/utils";

const RecentAlerts: FC<{ deviceId?: number; className?: string }> = observer(
  ({ deviceId, className }) => {
    const alarmService = useInject<AlarmService>(AlarmService);
    const alarmStore = useInject(AlarmStore);
    const navigate = useNavigate();

    const [deviceAlerts, setDeviceAlerts] = useState<Alarm[]>([]);
    const [autoRefresh] = useState(true);
    const [initalTime] = useState(() => DateTime.now());
    const [currentPage, setCurrentPage] = useState(1);

    const alerts = deviceId ? deviceAlerts : alarmStore.summary;
    const unreadCount = alerts.filter((a) => !a.isRead).length;

    const fetchAlarmDataByDevice = useCallback(async () => {
      if (!deviceId) return;
      try {
        const res = await alarmService.fetchAlarmDataByDevice(deviceId, null, DateTime.now(), 1);
        setDeviceAlerts(
          res.data
            .filter((a) => a.status === StatusEnum.ACTIVE)
            .sort((a, b) => b.time.diff(a.time, "milliseconds").milliseconds)
        );
      } catch (e) {
        console.error("Failed to fetch device alerts", e);
      }
    }, [deviceId, alarmService]);

    const fetchSummary = useCallback(async () => {
      await alarmStore.fetchSummary();
    }, [alarmStore]);

    // Fetch alarms when the component mounts
    useEffect(() => {
      if (deviceId) {
        fetchAlarmDataByDevice();
      } else {
        fetchSummary();
      }
    }, [deviceId, fetchAlarmDataByDevice, fetchSummary]);

    const handleAlertClick = useCallback(
      async (alert: Alarm) => {
        // Mark as read in API and Store
        try {
          await alarmStore.markAsRead(alert.id);
        } catch (e) {
          console.error("Failed to mark alarm as read", e);
        }

        // If it's a device alert, we also need to update local state if we want immediate feedback
        if (deviceId) {
          setDeviceAlerts((prev) =>
            prev.map((a) => (a.id === alert.id ? { ...a, isRead: true } : a))
          );
        }

        // Navigate to device detail
        if (alert.deviceId) {
          navigate({ to: "/dashboard/devices/$id", params: { id: alert.deviceId.toString() } });
        }
      },
      [alarmStore, deviceId, navigate]
    );

    const handleClearAlarm = useCallback(
      async (e: React.MouseEvent, alert: Alarm) => {
        e.stopPropagation();
        try {
          await alarmStore.markAsRead(alert.id);
          if (deviceId) {
            setDeviceAlerts((prev) =>
              prev.map((a) => (a.id === alert.id ? { ...a, isRead: true } : a))
            );
          }
        } catch (e) {
          console.error("Failed to mark alarm as read", e);
        }
      },
      [alarmStore, deviceId]
    );

    const isNewAlarm =
      (old: Alarm[]) =>
      (newAlarm: Alarm): boolean =>
        !old.some((oldAlarm) => oldAlarm.id === newAlarm.id);

    const fetchAlarm = useCallback(
      async (pageNumber: number, startDate: DateTime | null, endDate: DateTime) => {
        if (!deviceId) return;
        const res = await alarmService.fetchAlarmDataByDevice(
          deviceId,
          startDate,
          endDate,
          pageNumber
        );
        if (res.data.length === 0) return false;
        setDeviceAlerts((old) =>
          [...old, ...res.data.filter(isNewAlarm(old))].sort(
            (a, b) => b.time.diff(a.time, "milliseconds").milliseconds
          )
        );
        return true;
      },
      [deviceId, alarmService]
    );

    const refreshAlerts = useCallback(async () => {
      if (deviceId) {
        const now = DateTime.now();
        const newAlarms = await alarmService.fetchAllPaginatedAlarms(deviceId, initalTime, now);

        if (newAlarms.length > 0) {
          setDeviceAlerts((old) => {
            const newOrChangedAlarms = newAlarms.filter((a) => a.status === StatusEnum.ACTIVE);
            const clearedAlarms = newAlarms.filter((a) => a.status !== StatusEnum.ACTIVE);

            const newOld = old.filter(
              (a) =>
                !clearedAlarms.some((c) => c.id === a.id) &&
                !newOrChangedAlarms.some((c) => c.id === a.id)
            );
            return [...newOld, ...newOrChangedAlarms].sort(
              (a, b) => b.time.diff(a.time, "milliseconds").milliseconds
            );
          });
        }
      } else {
        await alarmStore.fetchSummary(true); // Force refresh on interval
      }
    }, [alarmService, deviceId, initalTime, alarmStore]);

    const [expandedAlarms, setExpandedAlarms] = useState<Set<string>>(new Set());

    const toggleExpand = useCallback((e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      setExpandedAlarms((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }, []);

    const getSeverityIcon = useCallback((_severity: Alarm["severity"]) => {
      return <Bell className="size-5 text-brand-accent" />;
    }, []);

    const getAlertMessage = useCallback((alert: Alarm) => {
      if (alert.display?.detailText) return alert.display.detailText;
      if (alert.display?.text) return alert.display.text;
      if (alert.text) return alert.text;

      return `Your smart water meter has triggered a 24-hour continuous flow alert on ${alert.time.toFormat("EEEE, dd MMMM")} at ${alert.time.toFormat("h:mm a")} at your property: ${alert.address || "Unknown Address"}.\n\nContinuous water use could be something as minor as a dripping tap or a concealed leak.`;
    }, []);

    const refreshAlertsRef = useRef(refreshAlerts);
    refreshAlertsRef.current = refreshAlerts;

    useEffect(() => {
      if (autoRefresh) {
        const interval = setInterval(() => {
          refreshAlertsRef.current();
        }, 10000);
        return () => clearInterval(interval);
      }
    }, [autoRefresh]);

    const onLoadingMore = useRef(false);
    const onScroll = useCallback<UIEventHandler<HTMLDivElement>>(
      (e) => {
        if (!deviceId) return;
        const target = e.target as HTMLDivElement;
        if (
          !onLoadingMore.current &&
          target.scrollTop + target.clientHeight >= target.scrollHeight * 0.8
        ) {
          onLoadingMore.current = true;
          fetchAlarm(currentPage + 1, null, initalTime).then((hasMore) => {
            if (hasMore) {
              setCurrentPage((prev) => prev + 1);
            }
            onLoadingMore.current = false;
          });
        }
      },
      [currentPage, fetchAlarm, initalTime, deviceId]
    );

    return (
      <Card className={cn("w-full", className)}>
        <CardHeader className="flex flex-row items-center justify-between gap-y-0 px-6 py-4">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-black tracking-tight text-slate-900 uppercase">
              {deviceId ? "Recent Alerts" : "Active Alerts Summary"}
            </CardTitle>
            {unreadCount > 0 && (
              <Badge className="bg-red-600 px-2 py-0.5 text-xs font-black">{unreadCount} New</Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={async (e) => {
                e.stopPropagation();
                await alarmStore.markAllAsRead();
                if (deviceId) {
                  await fetchAlarmDataByDevice();
                }
              }}
              className="h-8 border-slate-200 text-xs font-bold text-brand-accent-hover hover:bg-slate-50"
            >
              Mark all read
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex-1 px-4 pt-2 pb-4">
          {alerts.length > 0 ? (
            <div className="custom-scrollbar max-h-[500px] space-y-3 pr-1" onScroll={onScroll}>
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleAlertClick(alert);
                  }}
                  className={cn(
                    "group relative flex cursor-pointer overflow-hidden rounded-lg border-2 transition-all hover:border-brand-accent hover:shadow-md",
                    !alert.isRead
                      ? "border-brand-accent-subtle bg-brand-accent-light/50"
                      : "border-slate-100 bg-white"
                  )}
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start p-4">
                      <div className="mt-1 mr-4 rounded-full bg-white p-2 shadow-sm">
                        {getSeverityIcon(alert.severity)}
                      </div>

                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-base font-black tracking-tight text-slate-900 uppercase">
                            {alert.display?.title || (alert.type ? `${alert.type} Alert` : "Alert")}
                          </p>
                          <div className="flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                            <Clock className="mr-1 size-3" />
                            {getRelativeTime(alert.time)}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <p className="text-sm leading-relaxed font-medium text-slate-700">
                            {alert.display?.text || (
                              <>
                                Your smart water meter{" "}
                                {alert.serialNumber ? (
                                  <span className="font-bold">{alert.serialNumber}</span>
                                ) : (
                                  ""
                                )}{" "}
                                at your property{" "}
                                <span className="font-bold">
                                  {alert.address || "Unknown Address"}
                                </span>{" "}
                                has recorded a continuous water usage over the last 24 hours.
                              </>
                            )}
                          </p>
                        </div>

                        {deviceId && (
                          <div className="pt-1">
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 font-bold text-brand-accent hover:text-brand-accent-hover"
                              onClick={(e) => toggleExpand(e, alert.id)}
                            >
                              {expandedAlarms.has(alert.id) ? "Show less" : "Show more"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {deviceId && expandedAlarms.has(alert.id) && (
                      <div className="border-t border-slate-100 bg-slate-50 p-4">
                        <p className="text-sm leading-relaxed font-medium text-slate-600 whitespace-pre-line">
                          {getAlertMessage(alert)}
                        </p>
                      </div>
                    )}
                  </div>

                  {!alert.isRead && (
                    <>
                      <div className="absolute top-2 right-2 flex flex-col items-end gap-2">
                        <div className="size-2 rounded-full bg-brand-accent shadow-[0_0_8px_rgba(37,99,235,0.6)]" />
                      </div>
                      <div className="absolute right-2 bottom-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-7 rounded-full p-0 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-brand-accent-light hover:text-brand-accent focus:opacity-100"
                          onClick={(e) => handleClearAlarm(e, alert)}
                          title="Clear alarm"
                        >
                          <Check className="size-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground py-10 text-center">
              <Bell className="mx-auto mb-2 size-10 opacity-20" />
              <p>No active alerts</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
);

export default RecentAlerts;
